<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SearchFilter from '@/components/videos/SearchFilter.vue';
import CategoryBubble from '@/components/CategoryBubble.vue';
import AreaList from '@/components/AreaList.vue';
import SubjectArea from '@/components/SubjectArea.vue';
import SubjectList from '@/components/videos/SubjectList.vue';
import BreadcrumbsSimple from '@/components/BreadcrumbsSimple.vue';
import type { BreadcrumbsStep } from '@ilteducation/videos';
import { useCategories } from '@ilteducation/content-ref-data';
import { type SearchQuery } from '@ilteducation/content-search';
import { useVideos } from '@ilteducation/videos';
import { storeToRefs } from 'pinia';
import useGuiStore from '@/store/gui';
import { useLocale } from '@/plugins/translation';
import { useHead } from '@unhead/vue';
import capitalize from 'lodash/capitalize';
import config from '@/config/ConfigProvider';

const route = useRoute();
const router = useRouter();
const { categories } = useCategories();
const videoStore = useVideos();
const { searchResults } = storeToRefs(videoStore);

const { t, l } = useLocale();

const currentSubject = computed(() => categories.value.find((subject) => subject.slug === route.params.subject));

const serviceName = config.value('serviceName');
useHead({
  title: computed(() =>
    currentSubject.value
      ? t(l.videos.subject_page_title, { subject: currentSubject.value.category, service: capitalize(serviceName) })
      : capitalize(serviceName),
  ),
});

const breadcrumbsSteps = computed((): BreadcrumbsStep[] => [
  {
    name: currentSubject.value?.category ?? '',
    path: `/videos/${currentSubject.value?.slug ?? ''}`,
  },
]);

const contextId = ref('');
const searchQuery = ref<SearchQuery>({});

watch(
  currentSubject,
  () => {
    if (currentSubject.value === undefined) {
      router.push('/');
      return;
    }
    searchQuery.value = {
      filter: {
        subject: currentSubject.value.id,
      },
    };
    contextId.value = videoStore.search(searchQuery.value, true);
  },
  { immediate: true },
);

const singleSubjectVideos = computed(() => searchResults.value[contextId.value] ?? []);
const subjectArea = ref<{ ready: boolean; scrollToAnchor: (slug: string) => void }>();

const onAreaClicked = (slug: string) => {
  router.replace({ hash: `#${slug}` });
};
const { getThemeName } = useGuiStore();

// We watch ready as well, to scroll once again when all videos are loaded and the page is ready.
watch(
  () => [route.hash, subjectArea.value?.ready],
  async ([newHash]) => {
    if (!newHash) return;
    const slug = (newHash as string).slice(1);
    await nextTick();
    requestAnimationFrame(() => subjectArea.value?.scrollToAnchor(slug));
  },
  { immediate: true },
);
</script>

<template>
  <v-theme-provider with-background :theme="getThemeName(currentSubject?.slug)" class="bg-surface">
    <v-container class="custom-container py-8 px-4">
      <v-row>
        <v-container>
          <SearchFilter />
        </v-container>
      </v-row>
      <template v-if="currentSubject">
        <v-row class="breadcrumbs-section d-none d-md-flex mb-6 mx-md-0">
          <v-col class="px-0 d-flex align-self-center flex-column">
            <v-row no-gutters>
              <BreadcrumbsSimple :steps="breadcrumbsSteps" />
            </v-row>
            <h1 class="h2 text-subject ma-0">{{ currentSubject.category }}</h1>
          </v-col>
          <v-col class="d-flex justify-end px-0">
            <CategoryBubble :subject="currentSubject" size="medium" />
          </v-col>
        </v-row>
        <v-sheet class="bg-transparent">
          <v-row no-gutters class="area-container mb-2 pt-7 pb-10">
            <AreaList :subject="currentSubject" @area-clicked="onAreaClicked" />
          </v-row>
          <SubjectArea
            ref="subjectArea"
            :subject="currentSubject"
            :subject-videos="singleSubjectVideos"
            :is-search-results="false"
          />
        </v-sheet>
      </template>
      <v-row class="mt-8 mt-md-16" no-gutters>
        <h2 class="d-sr-only">{{ t(l.label_list_of_subjects) }}</h2>
        <SubjectList size="small" />
      </v-row>
    </v-container>
  </v-theme-provider>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

.custom-container {
  @media (min-width: 1920px) {
    max-width: 90rem !important;
  }
}

.area-container {
  position: relative;

  &:after {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 0;
    content: '';
    clear: both;
    height: 1px;
    background: var(--v-gray-lighten1);
    opacity: 0.5;
  }
}

.breadcrumbs-section {
  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
  }
}
</style>
